import { tierOf } from './operationRegistry';
import { findChipStep } from './mailWorkflows';
// ============================================================
// taskPlan — 複数の手順がある依頼は、先に手順の一覧を出してから進める（2026-10-30）
//
// 正: booking-platform/md/ai-control-center.md §16-6zf
//
// 前田さん: 「作業完了までの全てのステップを示してもらってから一つ一つの作業に取り掛かれるようにしたい。
//   例えば CDW を追加する → 請求書を変更する → メール返信する。プロセスを AI に理解させるのが大事」
//
// 流れ:
//   1. 書き込みが2つ以上ある依頼では、AI が最初に propose_plan で番号付きの手順を出す（手順カード）
//   2. スタッフが［この手順で進める］を押したら、1ステップずつ実行し、終わるたびに advance_plan で印を付ける
//   3. 途中で手順が変わったら propose_plan をもう一度呼ぶ（古いカードは「差し替え」になる）
//   4. 1ステップで終わる依頼は、手順カードを出さずに今までどおり進める
// 構造で縛る: 承認済みの手順が無いまま、1ターンに2つ目の書き込み（tier B）をしようとしたら
//   止めて propose_plan を求める（planGuard）。
// メールの標準手順のチップ（2026-10-30 追加）: plan のチップ（変更と起案が2つ以上・スタッフの操作が続くもの）は
//   **最初の**書き込み・起案の前に手順カードを求める。plan でないチップ（読み取り・起案だけ等）は止めない。
// ============================================================

/** 手順を実行するのは AI か、スタッフ（Square・サイドパネル・carmore の管理画面など AI ができない操作）か */
export type PlanActor = 'ai' | 'staff';
/** その手順で人に何を求めるか */
export type PlanCheck = 'card' | 'draft' | 'staff' | 'none';
export type PlanStepStatus = 'pending' | 'done' | 'skipped' | 'waiting_staff';

export interface PlanStep {
  label: string;
  actor: PlanActor;
  check: PlanCheck;
  status: PlanStepStatus;
  note?: string;
}

export interface TaskPlan {
  title: string;
  steps: PlanStep[];
}

export const PLAN_CHECK_LABELS: Record<PlanCheck, string> = {
  card: '確認カードで承認',
  draft: '下書きカードで送信',
  staff: 'スタッフが操作',
  none: '確認なし',
};

export const PLAN_STATUS_ICONS: Record<PlanStepStatus, string> = {
  pending: '⬜',
  done: '✅',
  skipped: '⏭️',
  waiting_staff: '🙋',
};

/**
 * AI が手順を組むときの手本（よくある業務プロセス）。システムプロンプトに入れる。
 * 書き方: 「何を（どのツールで）・誰が・どこで確認するか」。請求書（Square）は AI が作れないので必ずスタッフの手順にする。
 */
export const PROCESS_TEMPLATES: Array<{ key: string; label: string; when: string; steps: string[] }> = [
  {
    key: 'insurance_add',
    label: '補償の追加（CDW・NOC）',
    when: 'お客様が補償を付けたい・外したいと言ってきた',
    steps: [
      'CDW/NOC を付けて料金を再計算し反映する（apply_price_change・確認カード）',
      '請求書を直す（get_square_payment_status で状態を確かめる。未払いなら void_square_invoice → create_square_invoice で作り直す・確認カード。支払い済みなら差額だけの請求書をスタッフが Square で作る）',
      '追加の補償と金額をお客様に案内するメールを起案する（draft_reservation_email・下書きカードで送信）',
      '依頼メールをアーカイブする（archive_mail）',
    ],
  },
  {
    key: 'date_change',
    label: '日時の変更',
    when: '確定予約の日時を変えたい',
    steps: [
      '車両の重なりと同じ車種の空きを確かめる（check_schedule_conflict）',
      '料金への影響を確かめる（preview_price_change）',
      '日時と料金を反映する（apply_price_change または update_reservation・確認カード）',
      '請求書を直す（金額が変わらなければ不要。未払いなら void_square_invoice → create_square_invoice・確認カード。支払い済みなら差額をスタッフが Square で）',
      '変更を承ったメールを起案する（下書きカード）',
      '依頼メールをアーカイブする',
    ],
  },
  {
    key: 'cancel',
    label: 'キャンセル',
    when: 'お客様からキャンセルの依頼が来た',
    steps: [
      '証明書付きの無償キャンセルなら添付を確かめる（check_mail_attachments）',
      'キャンセル一式を実行する（cancel_reservation_full・ステータス／請求書の取消／メモ・確認カード）',
      'キャンセルを承ったメールを起案する（下書きカード）',
      '依頼メールをアーカイブする',
    ],
  },
  {
    key: 'request_accept',
    label: 'リクエスト予約の承諾',
    when: '自社サイトのリクエスト予約に在庫がある',
    steps: [
      '在庫を確かめる（list_available_vehicles）',
      '車両を割り当てる（assign_vehicle・確認カード。割り当て済みなら不要）',
      'ステータスをオファー中にする（update_reservation・確認カード）',
      '承諾メールをテンプレで起案する（下書きカード）',
      'フォローアップを設定し、通知メールをアーカイブする',
    ],
  },
  {
    key: 'group_status',
    label: '複数台予約（グループ）のステータス変更',
    when: '枝番 -1, -2, … の付いた予約のステータスを変える',
    steps: [
      'グループの予約を全部探す（search_reservations で基底の予約番号を検索）',
      '1台目のステータスを変える（update_reservation・確認カード）',
      '2台目以降も同じステータスに変える（1台ずつ確認カード）',
      'お客様へのメールは1台目の予約から1通だけ起案する（金額は全台の合計）',
    ],
  },
  {
    key: 'license_check',
    label: '免許証の確認',
    when: 'お客様から免許証の画像が届いた',
    steps: [
      '添付の免許証を判定する（check_mail_attachments・氏名と有効期限）',
      '免許確認をオンにする（set_reservation_checks・確認カード）',
      'お礼をスレッド返信で起案する（下書きカード）',
      'メールをアーカイブする',
    ],
  },
  {
    key: 'payment_done',
    label: '支払い完了の処理',
    when: '「請求書の支払いが完了」のメールが届いた・お客様から支払ったと連絡があった',
    steps: [
      '支払い済みの予約を確かめる（search_reservations。Square の金額と予約の合計が合っているか見る）',
      '支払い確認をオンにする（set_reservation_checks の payment・確認カード）',
      '事前決済の御礼メールを起案する（免許証のアップロードと事前受付のお願いを含む・下書きカード）',
      '支払い完了のメールをアーカイブする（archive_mail）',
    ],
  },
  {
    key: 'invoice_reissue',
    label: '失敗した請求書の再発行',
    when: 'お客様から「支払いができない」「新しい請求書を送ってほしい」と連絡が来た・請求書が FAILED になった',
    steps: [
      'Square の状況を確かめる（get_square_payment_status。請求書の状態・金額・決済の試み・予約の合計と合っているか。reissue.allowed が false なら止めてスタッフに理由を伝える）',
      '古い請求書を取り消す（void_square_invoice。直前に Square を読み直す。未払い・FAILED は Square で取り消し、取り消し済みなら予約から外すだけ、支払い済みなら止める。Square が受け付けなければ手順を止め、エラーと「管理画面で取り消してから『再度作成します』と指示」の案内を出す）',
      '新しい請求書を作って予約に結ぶ（create_square_invoice。金額と明細は古い請求書と同じ。違えば作らずに差を示して聞く。作ったら番号・状態・送り方を伝える）',
      'お客様への返信を起案する（新しい請求書を送ったこと・支払いの案内。draft_reservation_email・下書きカード。案を出すだけで、送信はオペレータ）',
      '依頼メールをアーカイブする（archive_mail）',
    ],
  },
  {
    key: 'accident_report',
    label: '事故報告（那覇）',
    when: 'スタッフから貸出中の事故・損傷の報告を受けた',
    steps: [
      '状況を聞き取る（発生日時・場所・車両番号・予約番号・事故の内容・ケガの有無・警察への届け出。分からない項目は聞く）',
      '同じ車両の事故記録と登録の形式を確かめる（get_accident_notion）',
      'Notion の事故連絡簿に記録を追記する（create_accident_notion・確認カード）',
      '予約のメモに事故の記録を残す（update_reservation のメモ追記・確認カード）',
      '修理・使えない期間がある場合は、その車両の後の予約を確かめて、入れ替えが要るものをスタッフに伝える（search_reservations）',
    ],
  },
  {
    key: 'vehicle_swap',
    label: '車両の入れ替え',
    when: '予約の号車を別の車両に替えたい（故障・修理・配車の都合）',
    steps: [
      '期間内に空いている同じ車種の号車を探す（list_available_vehicles）',
      '号車を替える（assign_vehicle・確認カード。車種が変わる入れ替えは AI ができないので、スタッフがサイドパネルで操作）',
      '入れ替えた理由を予約のメモに残す（update_reservation のメモ追記・確認カード）',
      '車種が変わる場合だけ、お客様への案内メールを起案する（下書きカード）',
    ],
  },
  {
    key: 'group_create',
    label: '複数台予約の作成',
    when: '電話・来店で2台以上をまとめて予約したい',
    steps: [
      '車種ごとの料金と在庫を確かめる（calculate_price。対象の車種が3台以上ならフリート割を案内する）',
      '予約を作る（create_reservation の quantity か vehicles・フォームで請求書を1枚にまとめるか聞く・確認カード）',
      '請求書を確かめて送る（スタッフが Square で操作。まとめた場合は下書きが1枚できているので、フリート割の割引行と合計を見てから送る）',
      'お客様への予約確認メールを1台目の予約から1通起案する（金額は全台の合計・下書きカード）',
    ],
  },
];

/** システムプロンプトに入れる「手順の一覧を出す」ルールと手本 */
export function buildPlanPrompt(): string {
  return [
    '# 複数の手順がある依頼は、先に手順の一覧を出す（2026-10-30 前田さん）',
    '',
    '- 依頼を終えるのに**書き込みが2つ以上**要る（予約の変更・料金の反映・割り当て・キャンセル・メールの起案・スタッフの Square 操作などの組み合わせ）なら、',
    '  **道具を使う前に propose_plan を呼び**、完了までの全ステップを番号付きで示す。各ステップに「誰が（AI／スタッフ）」「どこで確認するか（確認カード／下書きカード／スタッフが操作／なし）」を書く。',
    '  読み取り（検索・在庫・見積もり・メール本文を読む）は手順を組むための下調べなので、propose_plan の前にしてよい。',
    '- スタッフが手順カードで［この手順で進める］を押すまで、書き込みはしない。［手順を直す］なら、どこを直すかを聞いて propose_plan を出し直す。',
    '- 承認されたら1ステップずつ進め、**1つ終わるたびに advance_plan で印を付けてから**次へ進む（status=done）。手順に要らなくなったものは status=skipped と理由。',
    '- Square の請求書は、未払いの作り直し（void_square_invoice → create_square_invoice）だけ AI ができる。支払い済みの差額請求・返金・下書きの直しなど AI ができない操作は、スタッフの手順にする。そこに来たら advance_plan(status=waiting_staff) で何をしてほしいかを書き、',
    '  スタッフが「やった」「完了」と言うまで次へ進まない。',
    '- 途中で手順が変わった（スタッフの指示が変わった・調べたら前提が違った）ら、**残りの手順を propose_plan で出し直して**承認を受け直す。',
    '- **下の手本のプロセスに当たる依頼**（事故報告・車両の入れ替え・支払い完了・複数台予約の作成・請求書の再発行など）は、スタッフが1つしか言わなくても',
    '  手本の手順全体が要るので、**最初の書き込みの前に** propose_plan を呼ぶ（2026-10-30: 事故報告で Notion への追記だけをいきなり実行しかけた）。',
    '- **請求書の取り消し・作成はオペレータの指示があるときだけ**（2026-10-31 前田さん）。オペレータが「請求書を取り消し、再度作成します」「再発行して」と',
    '  はっきり言ったら、それが承認になる: propose_plan で「失敗した請求書の再発行」の手順を出し（承認ボタンは待たずに進行中になる）、そのまま void_square_invoice → create_square_invoice を実行してよい。',
    '  「状況を確認して」だけなら get_square_payment_status で読んで結果を伝えるだけにし、自分から取り消し・作成を提案して実行しない。',
    '- **書き込みが1つで終わる依頼**（ステータスを変えるだけ・メールを1通起案するだけ・見積もりだけ）は、propose_plan を使わず今までどおりすぐ進める。',
    '- メール起点の標準手順のチップのうち、指示文の末尾に「まず手順カードで…」と付いているもの（「リクエストOK（承諾）」「即確定」「キャンセル処理」など）は、',
    '  書き込みが1つに見えても**最初に propose_plan** を呼ぶ。手順はそのメールの標準手順と下の手本に合わせる。付いていないチップ（在庫の確認・起案だけ・アーカイブ）はすぐ実行する。',
    '',
    '## 手順の手本（よくある業務。予約の状態に合わせて増減してよい）',
    ...PROCESS_TEMPLATES.flatMap((p) => [
      `### ${p.label}（${p.when}）`,
      ...p.steps.map((s, i) => `${i + 1}. ${s}`),
    ]),
  ].join('\n');
}

const ACTORS: PlanActor[] = ['ai', 'staff'];
const CHECKS: PlanCheck[] = ['card', 'draft', 'staff', 'none'];

/** propose_plan の引数を検証して手順にする。おかしければ error を返す（AI に出し直させる） */
export function normalizePlanArgs(args: Record<string, any>): { plan?: TaskPlan; error?: string } {
  const title = String(args?.title ?? '').trim();
  const raw = Array.isArray(args?.steps) ? args.steps : [];
  if (!title) return { error: 'title（何の作業か）を指定してください' };
  if (raw.length < 2) return { error: '手順が1つなら propose_plan は不要です。そのまま実行してください' };
  if (raw.length > 12) return { error: '手順は12個までにまとめてください' };
  const steps: PlanStep[] = [];
  for (const s of raw) {
    const label = String((typeof s === 'string' ? s : s?.label) ?? '').trim();
    if (!label) return { error: '各手順に label（何をするか）を書いてください' };
    const actor: PlanActor = ACTORS.includes(s?.actor) ? s.actor : 'ai';
    let check: PlanCheck = CHECKS.includes(s?.check) ? s.check : (actor === 'staff' ? 'staff' : 'none');
    // 手順に書かれたツールから確認の場所を決める（AI の申告より実際の動きを優先。2026-10-30: 事故報告で
    // 確認カードの出る create_accident_notion を「確認なし」と書いた）
    if (actor === 'ai') {
      const tools: string[] = label.match(/[a-z]+(?:_[a-z]+)+/g) ?? [];
      if (tools.some((t) => isWriteTool(t))) check = 'card';
      else if (tools.includes('draft_reservation_email') && check === 'none') check = 'draft';
    }
    steps.push({ label: label.slice(0, 200), actor, check, status: 'pending' });
  }
  return { plan: { title: title.slice(0, 120), steps } };
}

/** advance_plan を手順に当てる。step は 1 始まり */
export function applyAdvance(plan: TaskPlan, args: Record<string, any>): { plan?: TaskPlan; error?: string; finished?: boolean } {
  const n = Number(args?.step);
  if (!Number.isInteger(n) || n < 1 || n > plan.steps.length) {
    return { error: `step は 1〜${plan.steps.length} の番号で指定してください` };
  }
  const status = String(args?.status ?? 'done') as PlanStepStatus;
  if (!['done', 'skipped', 'waiting_staff'].includes(status)) {
    return { error: 'status は done / skipped / waiting_staff のどれかです' };
  }
  const before = plan.steps.slice(0, n - 1).find((s) => s.status === 'pending');
  if (before && status === 'done') {
    const i = plan.steps.indexOf(before) + 1;
    return { error: `手順 ${i}「${before.label}」がまだです。順番に進めるか、飛ばすなら status=skipped で理由を書いてください` };
  }
  const note = String(args?.note ?? '').trim().slice(0, 300) || undefined;
  const steps = plan.steps.map((s, i) => (i === n - 1 ? { ...s, status, note } : s));
  const finished = steps.every((s) => s.status === 'done' || s.status === 'skipped');
  return { plan: { ...plan, steps }, finished };
}

/** 書き込み（tier B）のツールか。手順の数を数える対象 */
export function isWriteTool(name: string): boolean {
  return tierOf(name) === 'B' && name !== 'remember_rule';
}

/** メールの標準手順チップから来た指示か */
export function isWorkflowChipInstruction(text: string): boolean {
  return findChipStep(text) !== null;
}

/** 手順カードの前に止める「起案」（plan のチップでは、メールの起案も手順の1つとして扱う） */
const CHIP_ACTION_TOOLS = new Set(['draft_reservation_email', 'set_mail_followup']);

/**
 * 承認済みの手順が無いまま、1ターンに2つ目の書き込みをしようとしたら止める。
 * @param writesThisTurn このターンですでに成功した書き込みツールの名前
 * @returns 止めるときはツールの結果として AI に返す文。通すときは null
 */
/**
 * オペレータがチャットで請求書の取り消し・作成をはっきり指示したか（2026-10-31 前田さん:
 * 「失敗だった場合『請求書を取り消し、再度作成します』と答えます。オペレータの指示なので取り消しと作成が可能としたい」）。
 * 指示があれば、それを承認として扱う（手順カードは出すが承認ボタンは待たない・確認カードも出さない）。
 * 指示が無ければ AI は自分の判断で取り消し・作成をしない（従来どおり確認カードでスタッフが決める）。
 * 打ち消し（〜しないで・やめて・待って）と問いかけ（〜ますか？）は指示に数えない。
 */
export function operatorInvoiceInstruction(text: string): { void: boolean; create: boolean } {
  const t = String(text ?? '').replace(/\s+/g, '');
  const none = { void: false, create: false };
  if (!t) return none;
  if (/(しないで|しません|しない|やめ|止め|不要|待って|保留|まだ(取り?消|作)|ストップ)/.test(t)) return none;
  if (/(ますか|ですか|でしょうか|いい[？?]|べき[？?]|[？?])$/.test(t)) return none;
  const reissue = /再発行|発行し直|発行しなお/.test(t);
  // 「再度作成します」だけでも作成の指示に数える（取り消しが失敗したときの案内がこの言い方。2026-10-31）。
  // 取り消しは予約のキャンセルと紛れるので、請求書の話のときだけ数える
  const recreate = /再度作成|再作成|作り?直/.test(t);
  if (!/請求書|インボイス|invoice/i.test(t)) return { void: reissue, create: reissue || recreate };
  return {
    void: reissue || /(取り?消|取消|無効に)/.test(t),
    create: reissue || recreate || /(作成|作って|作りま|再度作|新しい請求書)/.test(t),
  };
}

/** 手順カードを、オペレータの指示を承認として即座に「進行中」にしてよいか（取り消し・作成を含む手順だけ） */
export function planApprovedByOperator(plan: TaskPlan, lastUserText: string): boolean {
  const op = operatorInvoiceInstruction(lastUserText);
  if (!op.void && !op.create) return false;
  const text = plan.steps.map((s) => s.label).join('\n');
  return (op.void && text.includes('void_square_invoice')) || (op.create && text.includes('create_square_invoice'));
}

/** 承認済みの手順カードが無いと1つ目でも実行しないツール（Square の請求書） */
export const PLAN_REQUIRED_TOOLS = new Set(['void_square_invoice', 'create_square_invoice']);

export function planGuard(
  toolName: string,
  writesThisTurn: string[],
  hasApprovedPlan: boolean,
  lastUserText: string,
): string | null {
  if (hasApprovedPlan) return null;
  // Square の請求書の取り消し・作成は、1つ目でも必ず手順カードの承認を先に受ける（2026-10-31 前田さん。
  // 再発行は「状況の確認 → 取り消し → 作成 → 返信」が一続きで、途中で止まると請求書の無い予約が残る）
  if (PLAN_REQUIRED_TOOLS.has(toolName)) {
    return `${toolName} は請求書の再発行の手順の一部です。ここで止まっています。`
      + '先に get_square_payment_status で状況を確かめ、propose_plan で「失敗した請求書の再発行」の手順を番号付きで示し、スタッフの承認を受けてから進めてください。';
  }
  const chip = findChipStep(lastUserText);
  if (chip) {
    // plan のチップは、最初の書き込み・起案の前に手順カード。plan でないチップは止めない
    if (!chip.plan) return null;
    if (!isWriteTool(toolName) && !CHIP_ACTION_TOOLS.has(toolName)) return null;
    return `このチップ「${chip.label}」は手順が複数あります（次: ${toolName}）。ここで止まっています。`
      + '先に propose_plan で完了までの手順（メールの標準手順に合わせる）を番号付きで示し、スタッフの承認を受けてから進めてください。';
  }
  if (!isWriteTool(toolName)) return null;
  if (writesThisTurn.length === 0) return null;
  return `この依頼は書き込みが2つ以上あります（済み: ${writesThisTurn.join('・')} → 次: ${toolName}）。`
    + 'ここで止まっています。完了までの残りの手順を propose_plan で番号付きで示し、スタッフの承認を受けてから進めてください。'
    + 'すでに終わった手順は、手順の一覧に含めて advance_plan で done にしてください。';
}
